import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import { fadeUp, slideInRight, stagger, viewport } from "../lib/motion";
import "../styles/Capabilities.css";

const QUERY = `*[_type == "capabilitiesSection"][0]{
  heading, highlightedText, featureCards
}`;

const ExpandableDescription = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 250; // Character limit for truncation

  if (!text) return null;

  // Replace literal \n sequences from Sanity with actual newlines
  const cleanText = text.replace(/\\n/g, '\n');
  const shouldTruncate = cleanText.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? cleanText : `${cleanText.substring(0, maxLength).trim()}...`;

  return (
    <div className="feature-description-wrapper">
      <p className="feature-description" style={{ whiteSpace: "pre-line" }}>
        {displayText}
        {shouldTruncate && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{
              color: "#000",
              cursor: "pointer",
              fontWeight: "600",
              marginLeft: "6px",
              textDecoration: "underline",
              fontSize: "14px"
            }}
          >
            {isExpanded ? "Show Less" : "Read More"}
          </span>
        )}
      </p>
    </div>
  );
};

const FeatureSection = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(res => {
      setData(res);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };
    handleResize(); // initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cards = data?.featureCards || [];

  const getImage = (img, imgMobile) => {
    const dUrl = img ? urlFor(img).url() : "";
    const mUrl = imgMobile ? urlFor(imgMobile).url() : dUrl;
    return isDesktop ? dUrl : mUrl;
  };

  return (
    <>
      {!isLoading && (
        <section className="feature-section" id="features">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.15)}
          >
            <motion.h2 className="feature-heading" variants={fadeUp}>
              {data.heading}{" "}
              <span className="highlighted-text">{data.highlightedText}</span>
            </motion.h2>
          </motion.div>

          <div className="feature-grid-complex">
            {cards.map((card, index) => (
              <motion.div
                className={`feature-card ${card.fullSpan ? "full-span horizontal-card" : ""}`}
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                variants={stagger(0.15, 0.05)}
              >
                {card.fullSpan ? (
                  <motion.div className="horizontal-content" variants={fadeUp}>
                    <motion.img
                      src={getImage(card.image, card.imageMobile)}
                      className="horizontal-image"
                      alt={card.title}
                      variants={slideInRight}
                    />
                    <div className="horizontal-text">
                      <h3 className="feature-title">{card.title}</h3>
                      <ExpandableDescription text={card.description} />
                    </div>
                  </motion.div>
                ) : (
                  <>
                    <motion.div className="feature-text" variants={fadeUp}>
                      <h3 className="feature-title">{card.title}</h3>
                      <ExpandableDescription text={card.description} />
                    </motion.div>
                    <motion.div className="feature-image-wrapper" variants={slideInRight}>
                      <img src={getImage(card.image, card.imageMobile)} alt={card.title} className="floating-ai-card" />
                    </motion.div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default FeatureSection;
